import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, LineElement, PointElement, LinearScale, CategoryScale, Title, Legend, Tooltip } from 'chart.js';

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Legend,
  Tooltip
);

const ZTestCalculator = () => {
  const [sampleMean, setSampleMean] = useState('');
  const [populationMean, setPopulationMean] = useState('');
  const [populationStdDev, setPopulationStdDev] = useState('');
  const [sampleSize, setSampleSize] = useState('');
  const [alpha, setAlpha] = useState('0.05');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const calculateZTest = () => {
    try {
      const sampleMeanValue = parseFloat(sampleMean);
      const populationMeanValue = parseFloat(populationMean);
      const populationStdDevValue = parseFloat(populationStdDev);
      const sampleSizeValue = parseInt(sampleSize);
      const alphaValue = parseFloat(alpha);

      if (isNaN(sampleMeanValue) || isNaN(populationMeanValue) || isNaN(populationStdDevValue) || isNaN(sampleSizeValue)) {
        setError('Please enter valid numbers for all fields');
        return;
      }

      if (sampleSizeValue <= 0) {
        setError('Sample size must be greater than 0');
        return;
      }

      if (isNaN(alphaValue) || alphaValue <= 0 || alphaValue >= 1) {
        setError('Please enter a valid alpha value between 0 and 1');
        return;
      }

      // Calculate z-value
      const zValue = (sampleMeanValue - populationMeanValue) / (populationStdDevValue / Math.sqrt(sampleSizeValue));

      // Calculate p-value (simplified)
      const pValue = 2 * (1 - Math.abs(zValue));

      // Generate sample data for visualization
      const generateSampleData = (mean, stdDev, size) => {
        const data = [];
        for (let i = 0; i < size; i++) {
          data.push(Math.random() * (stdDev * 2) + (mean - stdDev));
        }
        return data;
      };

      // Create chart data
      const chartData = {
        labels: Array.from({ length: sampleSizeValue }, (_, i) => i + 1),
        datasets: [
          {
            label: 'Sample Data',
            data: generateSampleData(sampleMeanValue, populationStdDevValue, sampleSizeValue),
            borderColor: '#2563eb',
            backgroundColor: 'rgba(37, 99, 235, 0.1)',
            tension: 0.1,
            pointRadius: 5,
            pointHoverRadius: 7,
          },
          {
            label: 'Population Mean',
            data: Array(sampleSizeValue).fill(populationMeanValue),
            borderColor: '#10b981',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            tension: 0.1,
            pointRadius: 5,
            pointHoverRadius: 7,
          }
        ]
      };

      setResult({
        zValue: zValue.toFixed(4),
        pValue: pValue.toFixed(4),
        alpha: alphaValue,
        rejectNull: pValue < alphaValue
      });

      setChartData(chartData);
      setError('');
    } catch (err) {
      setError('An error occurred while calculating the test. Please check your inputs.');
    }
  };

  return (
    <>
      <Helmet>
        <title>Z-Test Calculator - Hypothesis Testing</title>
        <meta name="description" content="Calculate Z-Test statistics for comparing sample mean to population mean. Use our interactive calculator to analyze your data." />
        <meta name="keywords" content="z-test calculator, hypothesis testing, statistics calculator, mean comparison" />
        <meta property="og:title" content="Z-Test Calculator - Hypothesis Testing" />
        <meta property="og:description" content="Calculate Z-Test statistics for comparing sample mean to population mean. Use our interactive calculator to analyze your data." />
        <meta name="twitter:title" content="Z-Test Calculator - Hypothesis Testing" />
        <meta name="twitter:description" content="Calculate Z-Test statistics for comparing sample mean to population mean. Use our interactive calculator to analyze your data." />
      </Helmet>

      <div className="statistics-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="calculator-header mb-12">
          <div className="flex items-center gap-4">
            <div className="header-icon text-4xl">🧬</div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Z-Test Calculator</h1>
              <p className="text-lg text-blue-600 font-semibold">Hypothesis Testing</p>
            </div>
          </div>
        </div>

        <div className="calculator-description space-y-4 mb-12">
          <p className="text-gray-700">
            Calculate Z-Test statistics for comparing sample mean to population mean. This calculator helps you analyze whether your sample mean significantly differs from the population mean.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
        >
          <div className="input-group mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="sampleMean">
              Sample Mean:
            </label>
            <input
              type="text"
              id="sampleMean"
              name="sampleMean"
              value={sampleMean}
              onChange={(e) => setSampleMean(e.target.value)}
              className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
              placeholder="Enter sample mean"
            />
          </div>

          <div className="input-group mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="populationMean">
              Population Mean:
            </label>
            <input
              type="text"
              id="populationMean"
              name="populationMean"
              value={populationMean}
              onChange={(e) => setPopulationMean(e.target.value)}
              className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
              placeholder="Enter population mean"
            />
          </div>

          <div className="input-group mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="populationStdDev">
              Population Standard Deviation:
            </label>
            <input
              type="text"
              id="populationStdDev"
              name="populationStdDev"
              value={populationStdDev}
              onChange={(e) => setPopulationStdDev(e.target.value)}
              className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
              placeholder="Enter population standard deviation"
            />
          </div>

          <div className="input-group mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="sampleSize">
              Sample Size:
            </label>
            <input
              type="text"
              id="sampleSize"
              name="sampleSize"
              value={sampleSize}
              onChange={(e) => setSampleSize(e.target.value)}
              className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
              placeholder="Enter sample size"
            />
          </div>

          <div className="input-group mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="alpha">
              Significance Level (α):
            </label>
            <input
              type="text"
              id="alpha"
              name="alpha"
              value={alpha}
              onChange={(e) => setAlpha(e.target.value)}
              className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
              placeholder="Enter significance level (e.g., 0.05)"
            />
          </div>

          <button
            onClick={calculateZTest}
            className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
          >
            Calculate Z-Test
          </button>

          {error && (
            <div className="text-red-500 text-center mt-4">
              {error}
            </div>
          )}

          {chartData && (
            <div className="mt-8">
              <h3 className="text-xl font-semibold mb-4">Data Visualization</h3>
              <div className="bg-white p-6 rounded-xl shadow-md">
                <Line
                  data={chartData}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: {
                        position: 'top',
                      },
                      title: {
                        display: true,
                        text: 'Sample vs Population Mean Comparison'
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true
                      }
                    }
                  }}
                />
              </div>
            </div>
          )}

          {result && (
            <div className="results-section mt-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Results</h3>
              
              <div className="grid grid-cols-1 gap-8 mb-12">
                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h4 className="text-xl font-semibold mb-4">Z-Test Results</h4>
                  <div className="space-y-2">
                    <p className="flex justify-between">
                      <span>Z-value</span>
                      <span className="font-medium">{result.zValue}</span>
                    </p>
                    <p className="flex justify-between">
                      <span>P-value</span>
                      <span className="font-medium">{result.pValue}</span>
                    </p>
                    <p className="flex justify-between">
                      <span>Significance Level (α)</span>
                      <span className="font-medium">{result.alpha}</span>
                    </p>
                    <p className="flex justify-between">
                      <span>Reject Null Hypothesis</span>
                      <span className={`font-medium ${result.rejectNull ? 'text-red-500' : 'text-green-500'}`}>
                        {result.rejectNull ? 'Yes' : 'No'}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Example Calculation <span className="ml-2 text-2xl">📝</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Let's analyze if a new teaching method improves student test scores:
          </p>
          
          <div className="bg-gray-50 p-6 rounded-lg">
            <h4 className="font-semibold mb-4">Sample Data:</h4>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h5 className="font-semibold mb-2">Sample Statistics:</h5>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Sample Mean: 85</li>
                  <li>Sample Size: 100</li>
                </ul>
              </div>
              <div>
                <h5 className="font-semibold mb-2">Population Parameters:</h5>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Population Mean: 80</li>
                  <li>Population SD: 15</li>
                </ul>
              </div>
            </div>
            
            <h4 className="font-semibold mt-4 mb-2">Results:</h4>
            <ul className="list-disc pl-8 space-y-2">
              <li>Z-value: 3.3333</li>
              <li>P-value: 0.0008</li>
              <li>Significance Level (α): 0.05</li>
              <li>Reject Null Hypothesis: Yes</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use Z-Test Calculator <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Gather Your Data</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Collect sample data (mean and size)</li>
                <li>Identify population parameters (mean and standard deviation)</li>
                <li>Choose your significance level (α)</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Enter Data</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Input sample mean in the first field</li>
                <li>Enter population mean in the second field</li>
                <li>Input population standard deviation</li>
                <li>Enter your sample size</li>
                <li>Set your desired significance level (α)</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Analyze Results</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Click "Calculate Z-Test" to get results</li>
                <li>Review the Z-value and P-value</li>
                <li>Check if the null hypothesis should be rejected</li>
                <li>Use the graph to visualize sample vs population mean</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Frequently Asked Questions <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-6">
            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">What is a Z-Test?</h3>
              <p className="text-gray-600">
                A Z-Test is a statistical test used to determine if a sample mean significantly differs from a known population mean when the population standard deviation is known.
              </p>
            </div>

            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">When should I use a Z-Test?</h3>
              <p className="text-gray-600">
                Use a Z-Test when:
                <ul className="list-disc pl-8 space-y-1">
                  <li>You have a large sample size (n ≥ 30)</li>
                  <li>The population standard deviation is known</li>
                  <li>Data follows a normal distribution</li>
                </ul>
              </p>
            </div>

            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">What does the Z-value mean?</h3>
              <p className="text-gray-600">
                The Z-value measures how many standard deviations your sample mean is from the population mean. A larger absolute Z-value indicates a stronger evidence against the null hypothesis.
              </p>
            </div>

            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">What if my sample size is small?</h3>
              <p className="text-gray-600">
                For small sample sizes, consider using a T-Test instead, as it's more appropriate when the population standard deviation is unknown or the sample size is small.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="text-4xl mb-4">📊</div>
              <h3 className="text-lg font-semibold mb-2">Statistical Analysis</h3>
              <p className="text-gray-600">Calculate Z-values and P-values for mean comparison</p>
            </div>
            <div className="text-center p-6 bg-green-50 rounded-lg">
              <div className="text-4xl mb-4">🎯</div>
              <h3 className="text-lg font-semibold mb-2">Easy Input</h3>
              <p className="text-gray-600">Simple interface for entering statistical parameters</p>
            </div>
            <div className="text-center p-6 bg-yellow-50 rounded-lg">
              <div className="text-4xl mb-4">🔄</div>
              <h3 className="text-lg font-semibold mb-2">Instant Results</h3>
              <p className="text-gray-600">Get immediate calculations and statistical significance</p>
            </div>
            <div className="text-center p-6 bg-purple-50 rounded-lg">
              <div className="text-4xl mb-4">📚</div>
              <h3 className="text-lg font-semibold mb-2">Educational Value</h3>
              <p className="text-gray-600">Learn about Z-Tests and hypothesis testing</p>
            </div>
            <div className="text-center p-6 bg-pink-50 rounded-lg">
              <div className="text-4xl mb-4">⚙️</div>
              <h3 className="text-lg font-semibold mb-2">Customizable</h3>
              <p className="text-gray-600">Adjust significance level for different requirements</p>
            </div>
            <div className="text-center p-6 bg-orange-50 rounded-lg">
              <div className="text-4xl mb-4">🔄</div>
              <h3 className="text-lg font-semibold mb-2">Multiple Uses</h3>
              <p className="text-gray-600">Perfect for educational research, quality control, and market analysis</p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Understanding Z-Tests <span className="ml-2 text-2xl">📊</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Z-Tests are statistical tests used to compare a sample mean to a known population mean. They help determine if the difference between the sample mean and population mean is statistically significant.
          </p>
          <ul className="list-none pl-8 space-y-4">
            <li className="flex items-center">
              <span className="h-3 w-3 bg-blue-600 rounded-full mr-2"></span>
              <span className="font-medium">Mean Comparison:</span> Testing if sample mean differs from population mean
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-green-600 rounded-full mr-2"></span>
              <span className="font-medium">Large Sample Tests:</span> Appropriate for large sample sizes (n ≥ 30)
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-yellow-600 rounded-full mr-2"></span>
              <span className="font-medium">Known Parameters:</span> Requires known population standard deviation
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-red-600 rounded-full mr-2"></span>
              <span className="font-medium">Research Applications:</span> Widely used in social sciences, education, and quality control
            </li>
          </ul>
          <p className="italic text-gray-500 mt-6">
            Note: Ensure your data meets the assumptions of the Z-Test (large sample size, known population standard deviation, normal distribution) before using this calculator.
          </p>
        </div>
      </div>
    </>
  );
};

export default ZTestCalculator;
